import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  useTheme,
  useMediaQuery,
  IconButton,
  Fab
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckIcon,
  ArrowBack,
  Payment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { clearCart } from '../../store/slices/cartSlice';
import { generateClient } from 'aws-amplify/api';
import { createOrder, createOrderItem } from '../../graphql/mutations';
import { surface, brand, ink, line, status } from '../../theme/tokens';
import Seo from '../../components/Common/Seo';

const SimpleCheckout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { items, totalAmount } = useSelector(state => state.cart);
  const { user } = useSelector(state => state.auth);
  
  const [formData, setFormData] = useState({
    // User contact information
    name: user?.name || '',
    contactMethod: 'email', // 'email' or 'whatsapp'
    email: user?.email || '',
    whatsapp: user?.phone || '', // Pre-fill with user's phone number
    
    // Delivery information (can be different from user)
    deliveryName: user?.name || '',
    deliveryPhone: user?.phone || '',
    deliveryAddress: '',
    deliveryLandmark: '',
    deliveryPincode: '',
    deliveryCity: '',
    
    notes: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Update form data when user changes (e.g., after login)
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
        whatsapp: user.phone || prev.whatsapp, // Update WhatsApp with user's phone
        deliveryName: user.name || prev.deliveryName,
        deliveryPhone: user.phone || prev.deliveryPhone,
      }));
    }
  }, [user]);

  const validateForm = () => {
    const newErrors = {};
    
    // User contact validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (formData.contactMethod === 'email' && !formData.email.trim()) {
      newErrors.email = 'Email is required for email contact method';
    }
    
    if (formData.contactMethod === 'whatsapp' && !formData.whatsapp.trim()) {
      newErrors.whatsapp = 'WhatsApp number is required for WhatsApp contact method';
    }
    
    // Delivery information validation
    if (!formData.deliveryName.trim()) {
      newErrors.deliveryName = 'Delivery contact name is required';
    }
    
    if (!formData.deliveryPhone.trim()) {
      newErrors.deliveryPhone = 'Delivery contact phone is required';
    }
    
    if (!formData.deliveryAddress.trim()) {
      newErrors.deliveryAddress = 'Delivery address is required';
    }
    
    if (!formData.deliveryCity.trim()) {
      newErrors.deliveryCity = 'City is required';
    }
    
    if (!formData.deliveryPincode.trim()) {
      newErrors.deliveryPincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.deliveryPincode.trim())) {
      newErrors.deliveryPincode = 'Please enter a valid 6-digit pincode';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const client = generateClient();
      
      // Generate a unique order number
      const orderNumber = `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 5).toUpperCase()}`;
      
      // Create the main order with complete user information captured at order time
      const orderData = {
        orderNumber: orderNumber,
        userId: user?.id || 'guest',
        status: 'PENDING',
        shippingAddress: JSON.stringify({
          deliveryName: formData.deliveryName,
          deliveryPhone: formData.deliveryPhone,
          address: formData.deliveryAddress,
          landmark: formData.deliveryLandmark,
          city: formData.deliveryCity,
          pincode: formData.deliveryPincode,
          fullAddress: `${formData.deliveryAddress}, ${formData.deliveryLandmark ? formData.deliveryLandmark + ', ' : ''}${formData.deliveryCity}, ${formData.deliveryPincode}`
        }),
        billingAddress: JSON.stringify({
          // User who placed the order (captured at order time)
          orderPlacedBy: {
            userId: user?.id || null,
            name: user?.name || formData.name,
            email: user?.email || formData.email,
            phone: user?.phone || formData.whatsapp,
            isRegisteredUser: !!user?.id
          },
          // Customer contact preferences
          customerName: formData.name,
          contactMethod: formData.contactMethod.toUpperCase(),
          email: formData.email,
          whatsapp: formData.whatsapp
        }),
        subtotal: totalAmount,
        tax: 0,
        shipping: 0,
        total: totalAmount,
        paymentMethod: 'PENDING',
        paymentStatus: 'PENDING',
        contactMethod: formData.contactMethod.toUpperCase(),
        whatsappNumber: formData.whatsapp,
        notes: formData.notes
      };
      
      console.log('Creating order with data:', orderData);
      
      const orderResult = await client.graphql({
        query: createOrder,
        variables: { input: orderData }
      });
      
      const orderId = orderResult.data.createOrder.id;
      console.log('Order created with ID:', orderId);
      
      // Create order items
      for (const item of items) {
        const orderItemData = {
          orderId: orderId,
          productId: item.id,
          quantity: item.quantity,
          price: item.price,
          total: item.totalPrice
        };
        
        await client.graphql({
          query: createOrderItem,
          variables: { input: orderItemData }
        });
      }
      
      console.log('Order and items created successfully');
      setSubmitted(true);
      dispatch(clearCart());
      
    } catch (error) {
      console.error('Error submitting order:', error);
      setErrors({ general: 'Failed to submit order. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToShopping = () => {
    navigate('/products');
  };

  const formatPrice = (price) => {
    const numPrice = Number(price) || 0;
    return `₹${numPrice.toFixed(2)}`;
  };

  if (submitted) {
    return (
      <Container 
        maxWidth="md" 
        sx={{ 
          py: { xs: 3, md: 4 }, 
          px: { xs: 2, sm: 3 } 
        }}
      >
        <Card
          elevation={0}
          sx={{ 
            textAlign: 'center', 
            p: { xs: 3, md: 5 },
            backgroundColor: surface.card,
            border: `1px solid ${line.subtle}`,
            borderRadius: 3,
          }}
        >
          <CheckIcon sx={{ 
            fontSize: { xs: 60, md: 80 }, 
            color: status.success, 
            mb: { xs: 1.5, md: 2 } 
          }} />
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              fontWeight: 700,
              letterSpacing: '-0.015em',
              color: status.success,
              mb: { xs: 1, md: 1 }
            }}
          >
            Request Submitted Successfully!
          </Typography>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              fontWeight: 600,
              color: ink.primary,
              mb: { xs: 2, md: 1 }
            }}
          >
            Thank you for your order request
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '0.9rem', md: '1rem' },
              lineHeight: 1.75,
              color: ink.secondary,
              px: { xs: 1, md: 0 }
            }}
          >
            We have received your order request and will contact you within 24 hours on your preferred contact method
            {formData.contactMethod === 'email' ? ' (Email)' : ' (WhatsApp)'} to confirm the details and arrange delivery.
          </Typography>
          
          <Box sx={{ 
            bgcolor: surface.sunken, 
            border: `1px solid ${line.subtle}`,
            textAlign: 'left',
            p: { xs: 2, md: 2.5 }, 
            borderRadius: 2, 
            mb: { xs: 2, md: 3 },
            mx: { xs: 1, md: 0 }
          }}>
            <Typography 
              variant="subtitle2" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '0.875rem', md: '0.875rem' },
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: ink.muted
              }}
            >
              Your contact details:
            </Typography>
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}
            >
              Name: {formData.name}
            </Typography>
            {formData.contactMethod === 'email' ? (
              <Typography 
                variant="body2"
                sx={{ 
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  wordBreak: 'break-all'
                }}
              >
                Email: {formData.email}
              </Typography>
            ) : (
              <Typography 
                variant="body2"
                sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}
              >
                WhatsApp: {formData.whatsapp}
              </Typography>
            )}
          </Box>
          
          <Button
            variant="contained"
            onClick={handleBackToShopping}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              px: { xs: 3, md: 4 },
              py: { xs: 1.5, md: 2 },
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            Continue Shopping
          </Button>
        </Card>
      </Container>
    );
  }

  if (items.length === 0) {
    return (
      <Container 
        maxWidth="md" 
        sx={{ 
          py: { xs: 3, md: 4 }, 
          px: { xs: 2, sm: 3 } 
        }}
      >
        <Card
          elevation={0}
          sx={{ 
            textAlign: 'center', 
            p: { xs: 3, md: 5 },
            backgroundColor: surface.card,
            border: `1px solid ${line.subtle}`,
            borderRadius: 3,
          }}
        >
          <CartIcon sx={{ 
            fontSize: { xs: 60, md: 80 }, 
            color: ink.muted, 
            mb: { xs: 1.5, md: 2 } 
          }} />
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              mb: { xs: 1, md: 1 }
            }}
          >
            Your cart is empty
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '0.9rem', md: '1rem' },
              px: { xs: 1, md: 0 }
            }}
          >
            Add some products to your cart before checkout
          </Typography>
          <Button
            variant="contained"
            onClick={handleBackToShopping}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              px: { xs: 3, md: 4 },
              py: { xs: 1.5, md: 2 },
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            Browse Products
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: { xs: 2, md: 4 }, 
        px: { xs: 1, sm: 2, md: 3 } 
      }}
    >
      <Seo
        title="Checkout"
        description="Confirm your details and place your order for farm-fresh produce."
        path="/checkout"
        noIndex
      />

      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: { xs: 3, md: 4 },
        flexDirection: { xs: 'row' },
        gap: { xs: 1, md: 2 }
      }}>
        <IconButton 
          onClick={() => navigate('/cart')}
          sx={{ 
            mr: { xs: 1, md: 1 },
            p: { xs: 1, md: 1.5 }
          }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h4" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              fontWeight: 700,
              letterSpacing: '-0.015em',
              color: ink.primary,
              mb: { xs: 0.5, md: 1 }
            }}
          >
            Order Request
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: { xs: '0.9rem', md: '1rem' },
              lineHeight: 1.7,
              color: ink.secondary,
              mb: { xs: 0, md: 0 }
            }}
          >
            Submit your order request and we'll contact you to confirm details and arrange delivery
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={{ xs: 2, md: 4 }}>
        {/* Order Summary */}
        <Grid item xs={12} lg={5}>
          <Card
            elevation={0}
            sx={{ 
              backgroundColor: surface.card,
              border: `1px solid ${line.default}`,
              borderRadius: 3,
              position: { lg: 'sticky' },
              top: { lg: 20 }
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ 
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  color: ink.primary,
                  mb: { xs: 2, md: 2 }
                }}
              >
                Order Summary
              </Typography>
              
              <List sx={{ 
                maxHeight: { xs: 'none', lg: '400px' },
                overflow: { xs: 'visible', lg: 'auto' }
              }}>
                {items.map((item, index) => (
                  <ListItem 
                    key={index} 
                    sx={{ 
                      px: 0,
                      py: { xs: 1, md: 1.5 }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={item.image}
                        alt={item.name}
                        sx={{ 
                          width: { xs: 48, md: 56 }, 
                          height: { xs: 48, md: 56 },
                          borderRadius: 2,
                          backgroundColor: surface.sunken,
                          border: `1px solid ${line.subtle}`
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body1"
                          sx={{ 
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            fontWeight: 600,
                            lineHeight: 1.3,
                            color: ink.primary
                          }}
                        >
                          {item.name}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="body2"
                            sx={{ 
                              fontSize: { xs: '0.8rem', md: '0.875rem' },
                              color: ink.muted,
                              mb: 0.5
                            }}
                          >
                            Quantity: {item.quantity}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: { xs: '0.8rem', md: '0.875rem' },
                              fontWeight: 700,
                              color: brand.mustardInk
                            }}
                          >
                            {formatPrice(item.price)} × {item.quantity} = {formatPrice(item.price * item.quantity)}
                          </Typography>
                        </Box>
                      }
                      sx={{ ml: { xs: 1.5, md: 2 } }}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Divider sx={{ my: { xs: 1.5, md: 2 }, borderColor: line.subtle }} />
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mb: 1 
              }}>
                <Typography sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  Subtotal:
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  {formatPrice(totalAmount)}
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mb: 1 
              }}>
                <Typography sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  Delivery:
                </Typography>
                <Typography 
                  sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, color: status.success, fontWeight: 600 }}
                >
                  To be discussed
                </Typography>
              </Box>
              <Divider sx={{ my: { xs: 1, md: 1 }, borderColor: line.subtle }} />
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between' 
              }}>
                <Typography 
                  variant="h6"
                  sx={{ 
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    fontWeight: 700,
                    color: ink.primary
                  }}
                >
                  Total:
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    fontWeight: 700,
                    color: brand.mustardInk
                  }}
                >
                  {formatPrice(totalAmount)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Form */}
        <Grid item xs={12} lg={7}>
          <Card
            elevation={0}
            sx={{
              backgroundColor: surface.card,
              border: `1px solid ${line.default}`,
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ 
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  color: ink.primary,
                  mb: { xs: 2, md: 2 }
                }}
              >
                Contact Information
              </Typography>
              
              {errors.general && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: { xs: 2, md: 3 },
                    fontSize: { xs: '0.8rem', md: '0.875rem' }
                  }}
                >
                  {errors.general}
                </Alert>
              )}
              
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      error={Boolean(errors.name)}
                      helperText={errors.name}
                      required
                      size={isMobile ? "medium" : "medium"}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl>
                      <FormLabel sx={{ 
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        mb: { xs: 1, md: 1 }
                      }}>
                        Preferred Contact Method
                      </FormLabel>
                      <RadioGroup
                        value={formData.contactMethod}
                        onChange={(e) => handleInputChange('contactMethod', e.target.value)}
                        row={!isMobile}
                        sx={{ 
                          flexDirection: { xs: 'column', md: 'row' },
                          gap: { xs: 1, md: 2 }
                        }}
                      >
                        <FormControlLabel
                          value="email"
                          control={<Radio size={isMobile ? "small" : "medium"} />}
                          label={
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            }}>
                              <EmailIcon sx={{ 
                                mr: 1, 
                                fontSize: { xs: '1.2rem', md: '1.5rem' }
                              }} />
                              <Typography sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                                Email
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="whatsapp"
                          control={<Radio size={isMobile ? "small" : "medium"} />}
                          label={
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            }}>
                              <WhatsAppIcon sx={{ 
                                mr: 1, 
                                fontSize: { xs: '1.2rem', md: '1.5rem' }
                              }} />
                              <Typography sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                                WhatsApp
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  
                  {formData.contactMethod === 'email' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="email"
                        label="Email Address"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        error={Boolean(errors.email)}
                        helperText={errors.email || 'We will send order confirmation to this email'}
                        required
                        size={isMobile ? "medium" : "medium"}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.9rem', md: '1rem' }
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.9rem', md: '1rem' }
                          },
                          '& .MuiFormHelperText-root': {
                            fontSize: { xs: '0.75rem', md: '0.8rem' }
                          }
                        }}
                      />
                    </Grid>
                  )}
                  
                  {formData.contactMethod === 'whatsapp' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="WhatsApp Number"
                        placeholder={user?.phone ? "Number from your profile" : "+91 85830 79287"}
                        value={formData.whatsapp}
                        onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                        error={Boolean(errors.whatsapp)}
                        helperText={
                          errors.whatsapp || 
                          (user?.phone && formData.whatsapp === user.phone 
                            ? 'Using phone number from your profile. You can modify if needed.' 
                            : 'We will contact you on WhatsApp for order confirmation'
                          )
                        }
                        required
                        size={isMobile ? "medium" : "medium"}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.9rem', md: '1rem' }
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.9rem', md: '1rem' }
                          },
                          '& .MuiFormHelperText-root': {
                            fontSize: { xs: '0.75rem', md: '0.8rem' }
                          }
                        }}
                      />
                    </Grid>
                  )}
                  
                  {/* Delivery Information Section */}
                  <Grid item xs={12}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        mb: { xs: 1.5, md: 2 },
                        mt: { xs: 1, md: 2 },
                        pt: { xs: 1.5, md: 2 },
                        borderTop: `1px solid ${line.subtle}`,
                        color: ink.primary,
                        fontWeight: 700,
                        letterSpacing: '-0.01em'
                      }}
                    >
                      Delivery Information
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Delivery Contact Name"
                      value={formData.deliveryName}
                      onChange={(e) => handleInputChange('deliveryName', e.target.value)}
                      error={Boolean(errors.deliveryName)}
                      helperText={errors.deliveryName || 'Person who will receive the delivery'}
                      required
                      size={isMobile ? "medium" : "medium"}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.75rem', md: '0.8rem' }
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Delivery Contact Phone"
                      value={formData.deliveryPhone}
                      onChange={(e) => handleInputChange('deliveryPhone', e.target.value)}
                      error={Boolean(errors.deliveryPhone)}
                      helperText={errors.deliveryPhone || 'Phone number for delivery coordination'}
                      required
                      size={isMobile ? "medium" : "medium"}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.75rem', md: '0.8rem' }
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={isMobile ? 2 : 3}
                      label="Delivery Address"
                      value={formData.deliveryAddress}
                      onChange={(e) => handleInputChange('deliveryAddress', e.target.value)}
                      error={Boolean(errors.deliveryAddress)}
                      helperText={errors.deliveryAddress || 'House/Flat number, Street, Area'}
                      required
                      size={isMobile ? "medium" : "medium"}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.75rem', md: '0.8rem' }
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Landmark (Optional)"
                      value={formData.deliveryLandmark}
                      onChange={(e) => handleInputChange('deliveryLandmark', e.target.value)}
                      placeholder="Near temple, school, market etc."
                      size={isMobile ? "medium" : "medium"}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Pincode"
                      value={formData.deliveryPincode}
                      onChange={(e) => handleInputChange('deliveryPincode', e.target.value)}
                      error={Boolean(errors.deliveryPincode)}
                      helperText={errors.deliveryPincode}
                      required
                      placeholder="731101"
                      size={isMobile ? "medium" : "medium"}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.75rem', md: '0.8rem' }
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.deliveryCity}
                      onChange={(e) => handleInputChange('deliveryCity', e.target.value)}
                      error={Boolean(errors.deliveryCity)}
                      helperText={errors.deliveryCity}
                      required
                      placeholder="Kolkata"
                      size={isMobile ? "medium" : "medium"}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.75rem', md: '0.8rem' }
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={isMobile ? 2 : 2}
                      label="Special Instructions (Optional)"
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      placeholder="Any special requests or delivery instructions..."
                      size={isMobile ? "medium" : "medium"}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Alert 
                      severity="info" 
                      sx={{ 
                        mb: { xs: 2, md: 2 },
                        '& .MuiAlert-message': {
                          fontSize: { xs: '0.8rem', md: '0.875rem' }
                        }
                      }}
                    >
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}
                      >
                        By submitting this request, you agree that we will contact you to confirm the order details, 
                        discuss delivery options, and provide final pricing including delivery charges.
                      </Typography>
                    </Alert>
                  </Grid>
                  
                  {/* Desktop buttons */}
                  {!isMobile && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => navigate('/cart')}
                          disabled={loading}
                          size="large"
                          sx={{ 
                            px: 3,
                            py: 1.5
                          }}
                        >
                          Back to Cart
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={loading}
                          size="large"
                          sx={{ 
                            flex: 1,
                            py: 1.5,
                            fontSize: '1rem'
                          }}
                        >
                          {loading ? 'Submitting Request...' : 'Submit Order Request'}
                        </Button>
                      </Box>
                    </Grid>
                  )}
                  
                  {/* Mobile buttons */}
                  {isMobile && (
                    <Grid item xs={12}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 1.5 
                      }}>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={loading}
                          fullWidth
                          size="large"
                          sx={{ 
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600
                          }}
                        >
                          {loading ? 'Submitting Request...' : 'Submit Order Request'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => navigate('/cart')}
                          disabled={loading}
                          fullWidth
                          size="medium"
                          sx={{ 
                            py: 1.5,
                            fontSize: '0.9rem'
                          }}
                        >
                          Back to Cart
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Mobile Submit FAB - only show when form is not in focus */}
      {isMobile && (
        <Fab
          variant="extended"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            left: 20,
            borderRadius: 28,
            height: 56,
            fontSize: '1rem',
            fontWeight: 600,
            zIndex: 1000
          }}
        >
          <Payment sx={{ mr: 1 }} />
          {loading ? 'Submitting...' : `Submit Request • ${formatPrice(totalAmount)}`}
        </Fab>
      )}
    </Container>
  );
};

export default SimpleCheckout;